import { useEffect, useState } from 'react';
import { create } from 'zustand';

/**
 * "Your advisor is getting ready" progress card.
 *
 * Opening an advisor session can take several seconds: the advisor reads the
 * idea's earlier sessions (the shared brief) and writes its first message.
 * Any start call wrapped in `withAdvisorPrep` shows this card if it takes
 * longer than a moment, stepping through what's happening, and hides the
 * instant the session is ready. Quick starts never show it.
 */

type Advisor = 'Product Strategist' | 'IP Consultant' | 'Financial Analyst';

const useAdvisorPrep = create<{ advisor: Advisor | null; set: (a: Advisor | null) => void }>((set) => ({
  advisor: null,
  set: (advisor) => set({ advisor }),
}));

/** Wrap an advisor-session start so members see progress while it prepares. */
export async function withAdvisorPrep<T>(advisor: Advisor, run: () => Promise<T>): Promise<T> {
  // Only surface the card if the start is actually slow.
  const t = setTimeout(() => useAdvisorPrep.getState().set(advisor), 900);
  try {
    return await run();
  } finally {
    clearTimeout(t);
    useAdvisorPrep.getState().set(null);
  }
}

const STEPS = [
  'Reading your earlier sessions on this idea',
  'Pulling together what you have already shared',
  'Preparing your first question',
];

export function AdvisorPrepCard() {
  const advisor = useAdvisorPrep((s) => s.advisor);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!advisor) return;
    setStep(0);
    const id = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 2200);
    return () => clearInterval(id);
  }, [advisor]);

  return (
    <div className={`advprep${advisor ? ' is-shown' : ''}`} role="status" aria-live="polite" aria-hidden={!advisor}>
      {advisor && (
        <div className="advprep__card">
          <span className="advprep__ring" aria-hidden="true" />
          <div className="advprep__text">
            <div className="advprep__title">Your {advisor} is getting ready</div>
            <div className="advprep__step" key={step}>
              {STEPS[step]}…
            </div>
            <div className="advprep__dots" aria-hidden="true">
              {STEPS.map((_, i) => (
                <span key={i} className={i <= step ? 'on' : ''} />
              ))}
            </div>
          </div>
        </div>
      )}
      <style>{`
        .advprep { position: fixed; left: 0; right: 0; bottom: calc(24px + env(safe-area-inset-bottom)); z-index: 1250;
          display: flex; justify-content: center; pointer-events: none; opacity: 0; transform: translateY(8px);
          transition: opacity 180ms ease, transform 180ms ease; }
        .advprep.is-shown { opacity: 1; transform: none; }
        html.has-tabbar .advprep { bottom: calc(80px + env(safe-area-inset-bottom)); }
        .advprep__card { display: flex; align-items: center; gap: 14px; min-width: min(360px, calc(100vw - 32px));
          max-width: calc(100vw - 32px); padding: 14px 18px; background: #fff; border: 1px solid var(--border-hairline);
          border-radius: 14px; box-shadow: 0 10px 30px rgba(15,23,20,0.12); }
        .advprep__ring { width: 28px; height: 28px; flex-shrink: 0; border-radius: 50%;
          border: 2px solid var(--brass); border-top-color: transparent; animation: spinSlow 1100ms linear infinite; }
        .advprep__text { display: grid; gap: 4px; min-width: 0; }
        .advprep__title { font: 600 14px/1.3 var(--f-sans); color: var(--ink); }
        .advprep__step { font: 400 13px/1.35 var(--f-sans); color: var(--text-decorative); animation: advprepIn 260ms ease; }
        .advprep__dots { display: flex; gap: 4px; margin-top: 2px; }
        .advprep__dots span { width: 18px; height: 3px; border-radius: 2px; background: rgba(15,23,20,0.1); transition: background 200ms ease; }
        .advprep__dots span.on { background: var(--heartwood); }
        @keyframes advprepIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .advprep, .advprep__step { transition: none; animation: none; } .advprep__ring { animation-duration: 3s; } }
      `}</style>
    </div>
  );
}
